import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { audiencesForRole, roleBaseline, cellState, nextTweak } from "@/lib/access";
import { PermCell, PermFilters, PermLegend, PermTable, usePermFilter } from "./PermTable";
import { usePermissionPreview } from "./PermissionPreview";

const EMPTY = { name: "", description: "", base_role: "customer", permissions: [], revoked: [] };

export default function RoleDialog({ open, onClose, role, catalog, audiences, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const filter = usePermFilter(catalog);
  const preview = usePermissionPreview();

  useEffect(() => {
    if (!open) return;
    setForm(role ? {
      name: role.name, description: role.description || "", base_role: role.base_role || "customer",
      permissions: [...(role.permissions || [])], revoked: [...(role.revoked || [])],
    } : EMPTY);
  }, [open, role]);

  const probe = useMemo(() => ({ role_id: role?.role_id, base_role: form.base_role }), [role, form.base_role]);
  const baseline = useMemo(() => roleBaseline(probe, audiences), [probe, audiences]);
  const audLabels = audiencesForRole(probe, audiences).map((a) => a.label);

  const stateOf = (perm) => cellState({
    inherited: baseline.has(perm), granted: form.permissions.includes(perm), revoked: form.revoked.includes(perm),
  });
  const click = (perm) => {
    const t = nextTweak(stateOf(perm));
    setForm((f) => ({
      ...f,
      permissions: t.granted ? [...new Set([...f.permissions, perm])] : f.permissions.filter((p) => p !== perm),
      revoked: t.revoked ? [...new Set([...f.revoked, perm])] : f.revoked.filter((p) => p !== perm),
    }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Role name is required"); return; }
    if (role && !(await preview.confirm(`/roles/${role.role_id}/preview`,
      { permissions: form.permissions, revoked: form.revoked, base_role: form.base_role }, `Save changes to ${role.name}?`))) return;
    setSaving(true);
    try {
      if (role) { await api.put(`/roles/${role.role_id}`, form); toast.success("Role updated"); }
      else { await api.post("/roles", form); toast.success("Custom role created"); }
      onSaved?.();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const effectiveCount = new Set([...baseline.keys(), ...form.permissions].filter((p) => !form.revoked.includes(p))).size;

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="role-dialog">
        <DialogHeader>
          <DialogTitle>{role ? `Edit role · ${role.name}` : "New custom role"}</DialogTitle>
          <DialogDescription>
            A role adjusts the audience defaults: grant extras or revoke things the audience gives.
            {audLabels.length ? <> Defaults come from <span className="font-medium text-slate-700">{audLabels.join(", ")}</span>.</> : " No audience matches this base role yet."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Role name</Label><Input data-testid="role-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Base role</Label>
              <Select value={form.base_role} onValueChange={(v) => setForm({ ...form, base_role: v })}>
                <SelectTrigger data-testid="role-base-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff (internal)</SelectItem>
                  <SelectItem value="partner">Partner</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea data-testid="role-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <Label>Permissions <span className="font-normal text-slate-400">· {effectiveCount} effective · {form.permissions.length} granted · {form.revoked.length} revoked</span></Label>
              <PermLegend />
            </div>
            <PermFilters filter={filter} testid="role-perm-filter" />
            <PermTable groups={filter.shown} maxHeight="max-h-[42vh]" testid="role-perm-table"
              columns={[{ id: "role", label: form.name || "This role", sub: "click to change" }]}
              renderCell={(perm) => {
                const from = baseline.get(perm);
                return (
                  <PermCell state={stateOf(perm)} onClick={() => click(perm)} testid={`role-perm-${perm}`}
                    title={stateOf(perm) === "inherited" && from ? `From audience defaults: ${from.join(", ")} — click to revoke` : undefined} />
                );
              }} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="save-role-button" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">{role ? "Save changes" : "Create role"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {preview.dialog}
    </>
  );
}
